import { useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import config from "../config.json";
import {
  ESmppBindType,
  ESmppInterfaceVersion,
  ESmppNumberingPlanIndicator,
  ESmppTypeOfNumber,
} from "../types/smpp/smpp.type";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

enum EConnectionProfileType {
  CLIENT,
  VENDOR,
}

export interface IClients {
  id: string;
  type: EConnectionProfileType;
  name: string;
  ip: string;
  systemId: string;
  password: string;
  systemType: string;
  bindType: ESmppBindType;
  active: boolean;
  sourceTon: ESmppTypeOfNumber;
  sourceNpi: ESmppNumberingPlanIndicator;
  destinationTon: ESmppTypeOfNumber;
  destinationNpi: ESmppNumberingPlanIndicator;
  maximumConnections: number;
  tps: number;
  smppVersion: ESmppInterfaceVersion;
  isDeleted: boolean;
  profileId: string;
  routingPlanId: string;
  port: number;
  windowSize?: string | number;
  isUp: boolean;
}

export const fetchClients = async (
  params: Params<IClients>
): Promise<IPageable<IClients>> => {
  try {
    console.log("Fetching clients...");
    const response = await axiosInstance.get("/connection-profiles/clients", {
    params: { ...params, search: params.search ?? "" },
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching clients:", error.message || error);
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the clients."
    );
  }
};

export async function fetchClient(id: string): Promise<IClients> {
  if (!id) {
    throw new Error("clients ID is required.");
  }

  try {
    const response = await axiosInstance.get(
      `/connection-profiles/clients/${id}`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching clients:", error);

    const errorMessage =
      error.response?.data?.message ||
      (error.response?.status === 404
        ? "Provider not found."
        : "An error occurred while getting the clients.");

    throw new Error(errorMessage);
  }
}



export const useClientsQuery = (
  params: Params<IClients>
): UseQueryResult<IPageable<IClients>, Error> => {
  const queryKey = ["clients", JSON.stringify(params)];
  return useQuery({
    queryKey,
    queryFn: () => fetchClients(params),
  });
};

export const useClientByIdQuery = (customerId: string): UseQueryResult<IClients|null, Error> => {
  return useQuery({
    queryKey: ["customer", customerId],
    queryFn: async () => {
      if (!customerId) return null;
      return await fetchClient(customerId);
    },
    select: (client) => client || null,
  });
};



export const getCustomerOptions = (customerId: string) => ({
  queryKey: ["customers", customerId],
  queryFn: async (): Promise<string> => {

    try {
      const client = await fetchClient(customerId); 
      return client?.name;
    } catch (error:any) {
      return "Error fetching customer";
    }
  },
});

export const useClientAccountQuery = (
  params: { name: string },
  options?: UseQueryOptions<IPageable<IClients>, Error>
): UseQueryResult<IPageable<IClients>, Error> => {
  const queryKey = ["clients", JSON.stringify(params)];
  return useQuery({
    queryKey,
    queryFn: () => fetchClients(params),
    ...options,
  });
};

export const getCustomerNamesByRoutingPlanId = (routingPlanId: string) => ({
  queryKey: ["customers", routingPlanId],
  queryFn: async (): Promise<string[]> => {
    const clientsPage = await fetchClients({ routingPlanId } as Params<IClients>);
    if (clientsPage.content && clientsPage.content.length > 0) {
      return clientsPage.content.map(client => client.name);
    }
    return [];
  },
  select: (names: string[]) => names,
});

export const useCustomerOptionsQuery = (
  routingPlanId: string,
  options?: Omit<UseQueryOptions<string[], Error>, "queryKey" | "queryFn">
): UseQueryResult<string[], Error> => {
  return useQuery({
    ...getCustomerNamesByRoutingPlanId(routingPlanId),
    ...options,
  });
};


